import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Briefcase, ArrowLeft, Save } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateJob } from 'features/job/hooks/useJobMutations';
import { useJob } from 'features/job/hooks/useJobs';
import { jobSchema } from 'features/job/validations/jobForm';
import { JobStatus, JobVisibility, Currency } from 'features/job/types/job.types';
import Layout from 'components/ui/Layout';

const EditJob = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { mutate: updateJob, isPending } = useUpdateJob();
  const { data: jobResponse, isLoading: jobLoading } = useJob(jobId);

  const job = jobResponse?.data?.job || jobResponse?.data;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(jobSchema),
    mode: 'onChange',
    defaultValues: {
      jobTitle: '',
      addressLine1: '',
      addressLine2: 'Not added',
      landmark: '',
      city: '',
      state: '',
      pincode: '',
      country: '',
      currency: 'INR',
      compensation: '',
      description: '',
      requirement: '',
      applicationDeadline: '',
      applicationDeadlineTime: '',
      applicationLink: '',
      visibility: 'Public',
      status: 'Active',
    },
  });

  useEffect(() => {
    if (job) {
      reset({
        jobTitle: job.jobTitle || '',
        addressLine1: job.address?.addressLine1 || '',
        addressLine2: job.address?.addressLine2 || '',
        landmark: job.address?.landmark || '',
        city: job.address?.city || '',
        state: job.address?.state || '',
        pincode: job.address?.pincode || '',
        country: job.address?.country || '',
        currency: job.currency || 'INR',
        compensation: job.compensation || '',
        description: job.description || '',
        requirement: job.requirement || '',
        applicationDeadline: job.applicationDeadline
          ? new Date(job.applicationDeadline).toISOString().split('T')[0]
          : '',
        applicationDeadlineTime: job.applicationDeadlineTime || '',
        applicationLink: job.applicationLink || '',
        visibility: job.visibility || 'Public',
        status: job.status || 'Active',
      });
    }
  }, [job, reset]);

  const onSubmit = (data) => {
    const payload = {
      jobId,
      data: {
        ...data,
        address: {
          addressLine1: data.addressLine1,
          addressLine2: data.addressLine2,
          landmark: data.landmark,
          city: data.city,
          state: data.state,
          pincode: data.pincode,
          country: data.country,
        },
      },
    };

    updateJob(payload, {
      onSuccess: () => {
        navigate('/jobs');
      },
    });
  };

  if (jobLoading) {
    return (
      <Layout>
        <div className='flex items-center justify-center h-screen'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary' />
        </div>
      </Layout>
    );
  }

  if (!job) {
    return (
      <Layout>
        <div className='flex flex-col items-center justify-center h-screen'>
          <div className='text-destructive text-lg mb-4'>Job not found</div>
          <button onClick={() => navigate('/jobs')} className='text-primary hover:underline'>
            Back to Jobs
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='p-6 max-w-6xl mx-auto'>
        {/* Header */}
        <div className='mb-6'>
          <button
            onClick={() => navigate('/jobs')}
            className='flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors'
          >
            <ArrowLeft size={16} />
            Back to Jobs
          </button>
          <h1 className='text-3xl font-bold flex items-center gap-2'>
            <Briefcase className='text-primary' size={32} />
            Edit Job
          </h1>
          <p className='text-muted-foreground mt-1'>Update job posting details</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className='bg-card border border-border rounded-lg p-6'>
          <div className='space-y-8'>
            {/* Basic Information */}
            <div>
              <h2 className='text-xl font-semibold text-foreground mb-4 pb-2 border-b border-border'>
                Basic Information
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {/* Job Title */}
                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-foreground mb-2'>
                    Job Title <span className='text-destructive'>*</span>
                  </label>
                  <input
                    type='text'
                    {...register('jobTitle')}
                    className={`w-full px-4 py-2.5 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all ${
                      errors.jobTitle ? 'border-destructive' : 'border-border'
                    }`}
                    placeholder='Enter job title'
                  />
                  {errors.jobTitle && <p className='mt-1.5 text-sm text-destructive'>{errors.jobTitle.message}</p>}
                </div>

                {/* Currency */}
                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>
                    Currency <span className='text-destructive'>*</span>
                  </label>
                  <select
                    {...register('currency')}
                    className={`w-full px-4 py-2.5 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all ${
                      errors.currency ? 'border-destructive' : 'border-border'
                    }`}
                  >
                    {Object.values(Currency).map((curr) => (
                      <option key={curr} value={curr}>
                        {curr}
                      </option>
                    ))}
                  </select>
                  {errors.currency && <p className='mt-1.5 text-sm text-destructive'>{errors.currency.message}</p>}
                </div>

                {/* Compensation */}
                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>Compensation</label>
                  <input
                    type='text'
                    {...register('compensation')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='e.g., 50,000 - 70,000 per month'
                  />
                </div>

                {/* Description */}
                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-foreground mb-2'>Description</label>
                  <textarea
                    {...register('description')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='Enter job description'
                    rows={6}
                  />
                </div>

                {/* Requirements */}
                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-foreground mb-2'>Requirements</label>
                  <textarea
                    {...register('requirement')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='Enter job requirements'
                    rows={6}
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div>
              <h2 className='text-xl font-semibold text-foreground mb-4 pb-2 border-b border-border'>Location</h2>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-foreground mb-2'>Address Line 1</label>
                  <input
                    type='text'
                    {...register('addressLine1')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='Street address'
                  />
                </div>

                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-foreground mb-2'>Address Line 2</label>
                  <input
                    type='text'
                    {...register('addressLine2')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='Apartment, suite, etc.'
                  />
                  
                </div>

                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>Landmark</label>
                  <input
                    type='text'
                    {...register('landmark')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='Nearby landmark'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>City</label>
                  <input
                    type='text'
                    {...register('city')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='City'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>State</label>
                  <input
                    type='text'
                    {...register('state')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='State'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>Pincode</label>
                  <input
                    type='text'
                    {...register('pincode')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='Pincode'
                  />
                </div>

                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-foreground mb-2'>Country</label>
                  <input
                    type='text'
                    {...register('country')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='Country'
                  />
                </div>
              </div>
            </div>

            {/* Application Details */}
            <div>
              <h2 className='text-xl font-semibold text-foreground mb-4 pb-2 border-b border-border'>
                Application Details
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>
                    Application Deadline <span className='text-destructive'>*</span>
                  </label>
                  <input
                    type='date'
                    {...register('applicationDeadline')}
                    className={`w-full px-4 py-2.5 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all ${
                      errors.applicationDeadline ? 'border-destructive' : 'border-border'
                    }`}
                  />
                  {errors.applicationDeadline && (
                    <p className='mt-1.5 text-sm text-destructive'>{errors.applicationDeadline.message}</p>
                  )}
                </div>

                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>Deadline Time</label>
                  <input
                    type='time'
                    {...register('applicationDeadlineTime')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                  />
                </div>

                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-foreground mb-2'>Application Link</label>
                  <input
                    type='url'
                    {...register('applicationLink')}
                    className={`w-full px-4 py-2.5 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all ${
                      errors.applicationLink ? 'border-destructive' : 'border-border'
                    }`}
                    placeholder='https://example.com/apply'
                  />
                  {errors.applicationLink && (
                    <p className='mt-1.5 text-sm text-destructive'>{errors.applicationLink.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Visibility & Status */}
            <div>
              <h2 className='text-xl font-semibold text-foreground mb-4 pb-2 border-b border-border'>
                Visibility & Status
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>
                    Visibility <span className='text-destructive'>*</span>
                  </label>
                  <select
                    {...register('visibility')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                  >
                    {Object.values(JobVisibility).map((vis) => (
                      <option key={vis} value={vis}>
                        {vis}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>
                    Status <span className='text-destructive'>*</span>
                  </label>
                  <select
                    {...register('status')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                  >
                    {Object.values(JobStatus).map((stat) => (
                      <option key={stat} value={stat}>
                        {stat}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className='flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-border'>
            <button
              type='button'
              onClick={() => navigate('/jobs')}
              className='flex-1 px-6 py-3 border border-border rounded-lg text-foreground hover:bg-accent transition-colors font-medium'
              disabled={isPending}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium'
              disabled={isPending}
            >
              {isPending ? (
                <div className='flex items-center justify-center'>
                  <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-primary-foreground mr-2' />
                  Updating...
                </div>
              ) : (
                <div className='flex items-center justify-center'>
                  <Save size={18} className='mr-2' />
                  Update Job
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default EditJob;
